import React from 'react'

import { AccountMetrics, PlatformOverview } from '@pages/Trade/types'

import { DxTradeAccountMetrics, DxTradeOrderTypes } from '@model/DxTrade.types'
import type { DxTradeOrderType, DxTradeSide, FixOrderTypeCode } from '@model/DxTrade.types'

export const roundToPrecision = (value: number, step: number) => {
  const precision = step.toString().split('.')[1]?.length || 0
  const factor = 10 ** precision
  return Math.round(value * factor) / factor
}

export const calculateSizeInLots = (
  quantity: number,
  lotSize: number,
  quantityIncrement?: number | null
): number => {
  const rawSizeLots = quantity / lotSize
  return quantityIncrement ? roundToPrecision(rawSizeLots, quantityIncrement) : rawSizeLots
}

export const clampQuantity = (
  value: number,
  min?: number | null,
  max?: number | null,
  fallback?: number
) => {
  const safeFallback = fallback ?? min ?? 0
  const normalized = Number.isFinite(value) ? value : safeFallback

  let result = normalized
  if (typeof min === 'number') {
    result = Math.max(result, min)
  }
  if (typeof max === 'number') {
    result = Math.min(result, max)
  }
  return result
}

export const snapToStep = (value: number, step?: number | null, origin?: number) => {
  if (!step || !Number.isFinite(value) || step <= 0) {
    return value
  }

  const base = origin ?? 0
  const steps = Math.round((value - base) / step)
  const snapped = base + steps * step
  return roundToPrecision(snapped, step)
}

export const computeNextStep = (
  current: number,
  direction: 1 | -1,
  step?: number | null,
  origin?: number
) => {
  if (!step || step <= 0) {
    return current
  }

  const base = origin ?? 0
  const alignedCurrent = snapToStep(current, step, base)
  const next = alignedCurrent + direction * step
  return roundToPrecision(next, step)
}

export const computeDisplayProps = (
  qtyLots: number,
  unit: 'units' | 'lots',
  lotSize?: number | null | undefined,
  minLots?: number | null | undefined,
  maxLots?: number | null | undefined,
  stepLots?: number | null | undefined
) => {
  const isUnits = unit === 'units' && lotSize != null

  const minBase = minLots ?? 0
  const minDisplay = isUnits && lotSize != null ? minBase * lotSize : minBase

  const maxDisplay: number | undefined = isUnits
    ? maxLots != null && lotSize != null
      ? maxLots * lotSize
      : maxLots ?? undefined
    : maxLots ?? undefined

  const stepDisplay: number | undefined = isUnits
    ? stepLots != null && lotSize != null
      ? stepLots * lotSize
      : undefined
    : stepLots ?? undefined

  const rawDisplayQty = isUnits && lotSize != null ? qtyLots * lotSize : qtyLots
  const qtyDisplay = stepLots
    ? roundToPrecision(
        rawDisplayQty,
        isUnits && lotSize != null ? (stepLots ?? 0) * lotSize : stepLots ?? 0
      )
    : rawDisplayQty

  return {
    qtyDisplay,
    minDisplay,
    maxDisplay,
    stepDisplay,
  }
}

// Helpers for price formatting/precision
export const getStepDecimals = (step?: number | null): number | undefined => {
  if (!step || step <= 0) return undefined
  const decimals = step.toString().split('.')[1]?.length || 0
  return decimals
}

export const formatValueWithStep = (value: number | '', step?: number | null): string => {
  if (value === '') return ''
  const decimals = getStepDecimals(step)
  return decimals != null ? value.toFixed(decimals) : String(value)
}

export const parseAndRoundToStep = (raw: string, step?: number | null): number | '' => {
  if (raw === '') return ''
  const num = Number(raw)
  if (!Number.isFinite(num)) return ''
  return step && step > 0 ? roundToPrecision(num, step) : num
}

export const computeExitPriceFromAmount = (
  amount: number | '',
  entryPrice?: number | null,
  quantity?: number | null,
  side?: DxTradeSide | null
): number | '' => {
  if (
    typeof amount !== 'number' ||
    typeof entryPrice !== 'number' ||
    typeof quantity !== 'number' ||
    !side ||
    (side !== 'BUY' && side !== 'SELL') ||
    quantity === 0
  ) {
    return ''
  }

  const delta = amount / quantity
  return side === 'BUY' ? entryPrice + delta : entryPrice - delta
}

export const formatDistanceDisplay = (
  exitPrice: number | '',
  entryPrice: number | null,
  pipSize?: number | null
): string => {
  if (typeof exitPrice !== 'number' || typeof entryPrice !== 'number') return '-'
  if (!pipSize || pipSize <= 0) return '-'

  const distance = Math.abs(exitPrice - entryPrice)
  const normalizedDistance = roundToPrecision(distance, pipSize)
  const pips = Math.round(normalizedDistance / pipSize)

  if (!Number.isFinite(entryPrice) || entryPrice === 0) {
    return `${pips}`
  }

  const percentValue = (distance / Math.abs(entryPrice)) * 100
  const roundedPercent = roundToPrecision(percentValue, 0.01)
  return `${pips} (${roundedPercent.toFixed(2)}%)`
}

export const validateStopLossPrice = (
  slPrice: number | '',
  entryPrice: number | null | undefined,
  side: DxTradeSide | null | undefined
): boolean => {
  if (typeof slPrice !== 'number' || typeof entryPrice !== 'number' || !side) {
    return true
  }
  if (slPrice <= 0) return false
  return side === 'BUY' ? slPrice < entryPrice : slPrice > entryPrice
}

export const validateTakeProfitPrice = (
  tpPrice: number | '',
  entryPrice: number | null | undefined,
  side: DxTradeSide | null | undefined
): boolean => {
  if (typeof tpPrice !== 'number' || typeof entryPrice !== 'number' || !side) {
    return true
  }
  if (tpPrice <= 0) return false
  return side === 'BUY' ? tpPrice > entryPrice : tpPrice < entryPrice
}

type TranslateFn = (key: string, options?: any) => string

export const getSlPriceErrorMessage = (
  slPrice: number | '',
  entryPrice: number | null | undefined,
  side: DxTradeSide | null | undefined,
  t: TranslateFn,
  priceStep?: number | null
): string | undefined => {
  if (typeof slPrice !== 'number' || typeof entryPrice !== 'number' || !side) {
    return undefined
  }

  if (typeof slPrice === 'number' && slPrice <= 0) {
    return t('OrderWidget.SL price must be greater than 0')
  }

  const isValid = validateStopLossPrice(slPrice, entryPrice, side)
  if (isValid) {
    return undefined
  }

  const key =
    side === 'BUY'
      ? 'OrderWidget.SL must be below entry price {{price}}'
      : 'OrderWidget.SL must be above entry price {{price}}'

  return t(key, {
    price: formatValueWithStep(entryPrice, priceStep),
  })
}

export const getTpPriceErrorMessage = (
  tpPrice: number | '',
  entryPrice: number | null | undefined,
  side: DxTradeSide | null | undefined,
  t: TranslateFn,
  priceStep?: number | null
): string | undefined => {
  if (typeof tpPrice !== 'number' || typeof entryPrice !== 'number' || !side) {
    return undefined
  }

  if (typeof tpPrice === 'number' && tpPrice <= 0) {
    return t('OrderWidget.TP price must be greater than 0')
  }

  const isValid = validateTakeProfitPrice(tpPrice, entryPrice, side)
  if (isValid) {
    return undefined
  }

  const key =
    side === 'BUY'
      ? 'OrderWidget.TP must be above entry price {{price}}'
      : 'OrderWidget.TP must be below entry price {{price}}'

  return t(key, {
    price: formatValueWithStep(entryPrice, priceStep),
  })
}

// Helpers for quantity formatting/precision

export const formatAmountForInput = (value: number | null): string => {
  if (value == null) return ''
  if (value === 0) return '0'
  return Math.abs(value).toFixed(2)
}

export const validatePriceIncrement = (
  priceInput: string,
  priceIncrement?: number | null
): boolean => {
  if (!priceInput || !priceIncrement || priceIncrement <= 0) {
    return true
  }

  const maxDecimals = getStepDecimals(priceIncrement)
  if (maxDecimals == null) {
    return true
  }
  const inputDecimals = priceInput.split('.')[1]?.length || 0

  if (inputDecimals > maxDecimals) {
    return false
  }

  const num = Number(priceInput)
  if (!Number.isFinite(num)) {
    return true
  }

  const convertedBack = num.toFixed(inputDecimals)
  const [intPart, decPart = ''] = priceInput.split('.')
  const normalizedInput = decPart ? `${intPart}.${decPart}` : intPart

  if (convertedBack !== normalizedInput) {
    return false
  }

  return true
}

export const getPriceIncrementErrorMessage = (
  priceInput: string,
  priceIncrement: number | null | undefined,
  t: TranslateFn
): string | undefined => {
  if (!priceInput || !priceIncrement || priceIncrement <= 0) {
    return undefined
  }

  const isValid = validatePriceIncrement(priceInput, priceIncrement)
  if (isValid) {
    return undefined
  }

  return t('OrderWidget.Price must be a multiple of increment {{increment}}', {
    increment: priceIncrement,
  })
}

export const formatDistancePipsForDisplay = (value: number): string => {
  return Math.round(value).toString()
}

export const preventNumericInputChars = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
    e.preventDefault()
  }
}

const DIGIT_PATTERN = /\d/
const INTEGER_PASTE_PATTERN = /^\d*$/
const DECIMAL_PASTE_PATTERN = /^\d+(\.\d*)?$/

const isModifierOrControlKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
  return event.ctrlKey || event.metaKey || event.key.length > 1
}

export const preventNonIntegerInputChars = (event: React.KeyboardEvent<HTMLInputElement>) => {
  if (isModifierOrControlKey(event)) return

  if (!DIGIT_PATTERN.test(event.key)) {
    event.preventDefault()
  }
}

export const preventNonIntegerPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
  const pasted = event.clipboardData.getData('text')
  if (!INTEGER_PASTE_PATTERN.test(pasted)) {
    event.preventDefault()
  }
}

export const preventNonDecimalInputChars = (event: React.KeyboardEvent<HTMLInputElement>) => {
  if (isModifierOrControlKey(event)) return
  if (DIGIT_PATTERN.test(event.key)) return

  if (event.key !== '.') {
    event.preventDefault()
    return
  }

  const value = event.currentTarget.value
  const hasDigitBefore = DIGIT_PATTERN.test(value)
  const hasDotAlready = value.includes('.')

  if (!hasDigitBefore || hasDotAlready) {
    event.preventDefault()
  }
}

export const preventNonDecimalPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
  const pasted = event.clipboardData.getData('text')
  if (!DECIMAL_PASTE_PATTERN.test(pasted)) {
    event.preventDefault()
  }
}

export const mapOrderTypeToFixOrdType = (
  orderType: DxTradeOrderType | undefined
): FixOrderTypeCode => {
  if (orderType === DxTradeOrderTypes.LIMIT) return '2'
  if (orderType === DxTradeOrderTypes.STOP) return '3'
  return '1'
}

export const formatExpireDateUtc = (date: Date | null | undefined): string | undefined => {
  if (!date) return undefined
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()
  return new Date(year, month, day, 0, 0, 0, 0).toISOString()
}

export function mapAccountMetrics({
  ws,
  rest,
}: {
  ws?: DxTradeAccountMetrics
  rest: PlatformOverview
}): AccountMetrics {
  const equity = ws?.equity ?? rest.equity ?? null
  const margin = ws?.margin ?? rest.usedMargin ?? null

  return {
    balance: ws?.balance ?? rest.balance ?? null,
    equity,
    openPL: ws?.openPL ?? null,
    marginFree: ws?.marginFree ?? rest.freeMargin ?? null,
    usedMargin: margin,
    marginLevel:
      equity != null && margin != null && margin > 0 ? Math.round((equity / margin) * 100) : null,
  }
}
